import * as React from 'react';
import { useDispatch } from 'react-redux';
import { loadOpenedSchema } from '../../sagas/dataSaga/dataSagaActions';
import { useSelector } from 'react-redux';
import { getIsLoadingOpenedSchema, getIsSchemaWasRemoved, getOpenedSchema, getOpenedSchemaModels, setIsSchemaWasRemoved } from '../../reducers/data/dataReducer';
import { Box, Button, Divider, IconButton, Tab, Tabs, Tooltip, Typography } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ShareIcon from '@mui/icons-material/Share';
import { openEditSchemaAction } from '../../sagas/modelSaga/modelSagaActions';
import { openAccessModel } from '../../sagas/accessSaga/accessSagaActions';
import { FlexBoxInline, PageDescriptionSx, PageHeaderSx, RemoveBtnSx, SecondaryBtnSx, TabSx, ThirdBtnSx, TooltipSlotProps } from '../../@shared/templates';
import TabsContent from './TabsContent';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { useSchema } from '../../hooks/useSchema';
import { Sizes } from '../../@shared/palette';
import SchemaShimmer from './SchemaShimmer';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { getAppConfig, getGuideStep, setGuideStep } from '../../reducers/main/mainReducer';
import GuideCallout from '../../components/GuideCallout';
import { GuideStepEnum } from '../../reducers/main/mainModel';
import PublishIcon from '@mui/icons-material/Publish';
import { PublishToCloudDialog } from './PublishToCloudDialog';

const Schema = () => {
  const navigate = useNavigate();
  const [isCopied, setIsCopied] = React.useState<boolean>(false)
  const [tabValue, setTabValue] = React.useState<number>(0);
  const [isConfirmOpen, setIsConfirmOpen] = React.useState(false);
  const [isPublishOpen, setIsPublishOpen] = React.useState(false);
  const { onRemoveSchema, isProcessing } = useSchema();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const isLoading = useSelector(getIsLoadingOpenedSchema);
  const isSchemaRemoved = useSelector(getIsSchemaWasRemoved);
  const appConfig = useSelector(getAppConfig)
  const guideStep = useSelector(getGuideStep);
  const schema = useSelector(getOpenedSchema);
  const schemaModels = useSelector(getOpenedSchemaModels);

  const handleEditSchema = React.useCallback(() => {
    if (schema?.id) {
      dispatch(openEditSchemaAction(schema.id))
    }
  }, [dispatch, schema?.id]);

  const handleShare = React.useCallback(() => {
    if (schema?.id) {
      dispatch(openAccessModel(schema.id))
    }
  }, [dispatch, schema?.id]);

  const handleRemoveSchema = React.useCallback(() => {
    if (schema?.id) {
      onRemoveSchema(schema.id)
    }
  }, [onRemoveSchema, schema?.id]);

  const onCopy = React.useCallback(async () => {
    if (schema?.id) {
      navigator.clipboard.writeText(schema?.id);
      setIsCopied(true);
    }
  }, [schema?.id]);

  React.useEffect(() => {
    if (isSchemaRemoved) {
      dispatch(setIsSchemaWasRemoved(false))
      navigate("/");
    }
  }, [dispatch, isSchemaRemoved, navigate])

  React.useEffect(() => {
    if (params.schemaId) {
      dispatch(loadOpenedSchema(params.schemaId))
    }
  }, [dispatch, params.schemaId])

  React.useEffect(() => {
    const tab = searchParams.get("tab");
    if (tab === "models") {
      setTabValue(1)
    }
  }, [searchParams])

  const handleTabChange = React.useCallback((event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    if (guideStep !== GuideStepEnum.None && newValue === 1) {
      dispatch(setGuideStep(GuideStepEnum.ClickDatasetAddBtn));
    }

    if (guideStep !== GuideStepEnum.None && newValue === 2) {
      dispatch(setGuideStep(GuideStepEnum.ClickAddModelBtn));
    }
  }, [dispatch, guideStep]);

  if (isLoading || !schema) {
    return <SchemaShimmer />
  }

  return <Box sx={{ padding: "15px", width: "100%" }}>
    <ConfirmDialog isOpen={isConfirmOpen} onClose={() => setIsConfirmOpen(false)} title='Remove schema' description='Removing needs to be confirmed' onConfirmedAction={handleRemoveSchema} isConfirmDisabled={isProcessing} isProcessing={isProcessing} />
    <PublishToCloudDialog isOpen={isPublishOpen} onClose={() => setIsPublishOpen(false)} schemaId={schema!.id} schemaName={schema!.name} schemaItems={schema!.schemaItems ?? []} models={schemaModels ?? []} />
    <Box sx={{ marginBottom: "20px" }}>
      <Box sx={{ ...FlexBoxInline, gap: "10px" }}>
        <Typography sx={PageHeaderSx}>
          {schema?.name}
        </Typography>

        <Box sx={{ ...FlexBoxInline, gap: "5px" }}>
          <Tooltip title={"Edit"} placement="bottom" arrow>
            <IconButton type="button" sx={{ ...SecondaryBtnSx, p: "5px", border: "none" }} aria-label="Edit" onClick={handleEditSchema}>
              <EditIcon sx={{ fontSize: Sizes.icon }} />
            </IconButton>
          </Tooltip>
          {!appConfig.isSelfHostedServiceEnabled && <Tooltip title={"Share"} placement="bottom" arrow>
            <IconButton type="button" sx={{ ...SecondaryBtnSx, p: "5px", border: "none" }} aria-label="Share" onClick={handleShare}>
              <ShareIcon sx={{ fontSize: Sizes.icon }} />
            </IconButton>
          </Tooltip>}
          {appConfig.isSelfHostedServiceEnabled && <Tooltip title={"Publish to cloud"} placement="bottom" arrow>
            <IconButton type="button" sx={{ ...SecondaryBtnSx, p: "5px", border: "none" }} aria-label="Publish" onClick={() => setIsPublishOpen(true)}>
              <PublishIcon sx={{ fontSize: Sizes.icon }} />
            </IconButton>
          </Tooltip>}
          <Tooltip title={"Remove"} placement="bottom" arrow>
            <IconButton type="button" sx={{ ...RemoveBtnSx, p: "5px", border: "none" }} aria-label="Remove" onClick={() => setIsConfirmOpen(true)}>
              <DeleteIcon sx={{ fontSize: Sizes.icon }} />
            </IconButton>
          </Tooltip>
        </Box>

      </Box>

      {schema?.description && <Typography sx={PageDescriptionSx}>
        {schema?.description}
      </Typography>}
      <Box sx={{ ...FlexBoxInline, marginBottom: "20px" }}>
        <Typography sx={{ ...PageDescriptionSx, margin: 0 }}>Id:</Typography>
        <Tooltip title={isCopied ? "Copied!" : "Copy"} placement="right" arrow>
          <Button sx={{ ...ThirdBtnSx }} onClick={onCopy}>{schema?.id}</Button>
        </Tooltip>
      </Box>
    </Box>

    <Tabs value={tabValue} onChange={handleTabChange}>
      <Tab sx={TabSx} label="Readme" />
      <Tooltip placement="bottom" open={guideStep === GuideStepEnum.OpenDatasetTab} slotProps={TooltipSlotProps} title={
        <GuideCallout>
          Click to open tab to collect data for your schema. Data should be in the format defined by your schema structure. After adding data you will be able to train your model and make predictions.
        </GuideCallout>
      } arrow>
        <Tab sx={TabSx} label="Data" />
      </Tooltip>
      <Tooltip placement="bottom" open={guideStep === GuideStepEnum.OpenModelTab} slotProps={TooltipSlotProps} title={
        <GuideCallout>
          Click to open tab to create new model for your schema.
        </GuideCallout>
      } arrow>
        <Tab sx={TabSx} label="Models" />
      </Tooltip>
      <Tab sx={TabSx} label="Schema" />
      <Tab sx={TabSx} label="Monitoring" />
    </Tabs>
    <Divider sx={{ marginBottom: "20px" }} />

    <TabsContent isHasDescription={!!schema?.description} schemaItems={schema!.schemaItems} tabId={tabValue} schemaId={schema!.id} schema={schema} schemaName={schema!.name} />
  </Box>
}

export default Schema;
